import { isNil } from 'lodash/fp';
import { useCallback } from 'react';
import { type MatrixSpecsFragment } from '../../../../api/fragments/MatrixSpecs';
import { type TrimBlockSpecsFragment } from '../../../../api/fragments/TrimBlockSpecs';
import { useCreateTrimBlockMutation } from '../../../../api/mutations/createTrimBlock';
import { useUpdateTrimBlockMutation } from '../../../../api/mutations/updateTrimBlock';
import { BlockType } from '../../../../api/types';
import useHandleTrimMatrixAsset from './useHandleTrimMatrixAsset';
import useHandleTrimSettingAsset from './useHandleTrimSettingAsset';
import { type TrimBlockFormValues, type TrimBlockInput, type TrimSettingFormValues } from './utils';

const useHandleBlockSave = () => {
    const [createTrimBlockMutation] = useCreateTrimBlockMutation();
    const [updateTrimBlockMutation] = useUpdateTrimBlockMutation();

    const { uploadTrimSettingAsset, deleteTrimSettingAsset } = useHandleTrimSettingAsset();

    const handleTrimMatrixAsset = useHandleTrimMatrixAsset();

    const handleTrimMatrix = useCallback(
        async (
            variantConfiguratorId: string,
            trimBlock: TrimBlockSpecsFragment,
            matrix: MatrixSpecsFragment[],
            trimSettings: TrimSettingFormValues[]
        ) => {
            const colorTrimCombo = matrix.reduce((accumulator, currentValue) => {
                const previousColorAccumulator = { ...accumulator[currentValue.trimId] };

                return {
                    ...accumulator,
                    [currentValue.trimId]: {
                        ...previousColorAccumulator,
                        [currentValue.colorId]: currentValue.variantTrimImage,
                    },
                };
            }, {});

            // begin upload section images, which is matrix
            const colorMatrixAsset = trimSettings?.flatMap(({ sectionImages, id }, index) => {
                const trimId = id ?? trimBlock?.trimSettings[index]?.id;

                return (sectionImages || []).map(sectionImage => {
                    if (!sectionImage.colorId) {
                        return Promise.resolve();
                    }

                    const initialVariantTrimImage = colorTrimCombo?.[trimId]?.[sectionImage.colorId];

                    return handleTrimMatrixAsset(
                        variantConfiguratorId,
                        sectionImage.colorId,
                        trimId,
                        sectionImage.variantTrimImage,
                        initialVariantTrimImage
                    );
                });
            });

            if (colorMatrixAsset.length) {
                await Promise.all(colorMatrixAsset);
            }
        },
        [handleTrimMatrixAsset]
    );

    const createTrimBlock = useCallback(
        async (variantConfiguratorId: string, trimBlockInput: TrimBlockInput) => {
            const { isActive, sectionTitle, trimSettings } = trimBlockInput;

            const { data } = await createTrimBlockMutation({
                variables: {
                    settings: {
                        type: BlockType.Trim,
                        trimBlockInput: {
                            isActive,
                            sectionTitle,
                            trimSettings: trimSettings.map(({ image, sectionImages, ...rest }) => rest),
                        },
                    },
                    variantConfiguratorId,
                },
            });

            const trimBlock = data?.variantConfigurator?.trimBlock;

            const trimSettingAssetToUpload = trimSettings?.map((setting, index) => {
                // can use index because the creation using same input
                const foundSetting = trimBlock?.trimSettings[index];

                if (setting.image instanceof File) {
                    return uploadTrimSettingAsset(
                        variantConfiguratorId,
                        trimBlock?.id,
                        foundSetting?.id,
                        setting.image
                    );
                }

                return Promise.resolve();
            });

            if (trimSettingAssetToUpload.length) {
                await Promise.all(trimSettingAssetToUpload);
            }

            await handleTrimMatrix(variantConfiguratorId, trimBlock, data?.variantConfigurator?.matrix, trimSettings);
        },
        [createTrimBlockMutation, handleTrimMatrix, uploadTrimSettingAsset]
    );

    const updateTrimBlock = useCallback(
        async (
            variantConfiguratorId: string,
            blockId: string,
            trimBlockInput: TrimBlockInput,
            initialBlock: TrimBlockSpecsFragment
        ) => {
            const { isActive, sectionTitle, trimSettings } = trimBlockInput;

            // delete asset first
            // detect from initial block first
            const trimSettingAssetsToDelete = initialBlock?.trimSettings
                .filter(initialSetting => {
                    const foundSetting = trimSettings.find(setting => setting?.id && setting.id === initialSetting.id);

                    return isNil(foundSetting?.image);
                })
                .map(setting => deleteTrimSettingAsset(variantConfiguratorId, blockId, setting.id));

            if (trimSettingAssetsToDelete.length) {
                await Promise.all(trimSettingAssetsToDelete);
            }

            // begin update trim block
            const { data } = await updateTrimBlockMutation({
                variables: {
                    settings: {
                        type: BlockType.Trim,
                        trimBlockInput: {
                            isActive,
                            sectionTitle,
                            trimSettings: trimSettings.map(({ image, sectionImages, ...rest }) => rest),
                        },
                    },
                    variantConfiguratorId,
                    blockId,
                },
            });

            const trimBlock = data?.variantConfigurator?.trimBlock;

            const colorSettingAssetsToUpload = trimSettings?.map((setting, index) => {
                if (setting.image instanceof File) {
                    // It should be the same index as the input
                    const settingId = setting.id ?? trimBlock?.trimSettings[index]?.id;

                    return uploadTrimSettingAsset(variantConfiguratorId, blockId, settingId, setting.image);
                }

                return Promise.resolve();
            });

            if (colorSettingAssetsToUpload.length) {
                await Promise.all(colorSettingAssetsToUpload);
            }

            await handleTrimMatrix(variantConfiguratorId, trimBlock, data?.variantConfigurator?.matrix, trimSettings);
        },
        [deleteTrimSettingAsset, handleTrimMatrix, updateTrimBlockMutation, uploadTrimSettingAsset]
    );

    return useCallback(
        async (values: TrimBlockFormValues, variantConfiguratorId: string, initialBlock: TrimBlockSpecsFragment) => {
            const { trimBlockInput } = values;
            const { id, ...settings } = trimBlockInput;

            if (id) {
                return updateTrimBlock(variantConfiguratorId, id, settings, initialBlock);
            }

            return createTrimBlock(variantConfiguratorId, settings);
        },
        [createTrimBlock, updateTrimBlock]
    );
};

export default useHandleBlockSave;
